import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setCustomerAssignments } from '../api/client';
import type { TenantMember, CustomerAssignedMember } from '../api/client';
import type { RivhitCustomer } from '../context/RivhitDataContext';

interface RowFormState {
  last_name: string;
  first_name: string;
  phone: string;
  email: string;
  city: string;
}

function toRowForm(c: RivhitCustomer): RowFormState {
  return {
    last_name: c.last_name ?? '',
    first_name: c.first_name ?? '',
    phone: c.phone ?? '',
    email: c.email ?? '',
    city: c.city ?? '',
  };
}

// ---- TeamCombobox ----

interface TeamComboboxProps {
  members: TenantMember[];
  selectedIds: Set<string>;
  onToggle: (userId: string) => void;
  disabled?: boolean;
}

function TeamCombobox({ members, selectedIds, onToggle, disabled }: TeamComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = search.trim()
    ? members.filter(
        (m) =>
          (m.name || '').toLowerCase().includes(search.toLowerCase()) ||
          (m.email || '').toLowerCase().includes(search.toLowerCase()),
      )
    : members;

  const selectedMembers = members.filter((m) => selectedIds.has(m.user_id));

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        data-testid="team-combobox-trigger"
        aria-label={t('assignments.assignedTeam', 'Assigned team')}
        aria-expanded={open}
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        className={[
          'flex flex-wrap gap-1 min-h-[30px] w-full items-center px-2 py-1 rounded border text-left text-xs transition-colors',
          disabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : open
            ? 'border-primary-500 ring-1 ring-primary-500 bg-white'
            : 'border-gray-300 bg-white hover:border-primary-400 cursor-pointer',
        ].join(' ')}
      >
        {selectedMembers.length === 0 ? (
          <span className="text-gray-400 text-xs">—</span>
        ) : (
          <>
            {selectedMembers.slice(0, 2).map((m) => (
              <span
                key={m.user_id}
                className="inline-flex items-center rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-700 max-w-[80px] truncate"
              >
                {m.name?.split(' ')[0] || m.email?.split('@')[0] || '?'}
              </span>
            ))}
            {selectedMembers.length > 2 && (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                +{selectedMembers.length - 2}
              </span>
            )}
          </>
        )}
        <span className="ltr:ml-auto rtl:mr-auto text-gray-400 text-[10px] shrink-0">▾</span>
      </button>

      {open && (
        <div className="absolute z-50 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-xl ltr:left-0 rtl:right-0">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              data-testid="team-combobox-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('assignments.searchPlaceholder', 'Search members…')}
              className="w-full text-xs rounded border border-gray-300 px-2 py-1 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              autoFocus
            />
          </div>
          <ul className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <li className="px-3 py-3 text-xs text-gray-400 text-center">
                {t('assignments.noMembersFound', 'No members found')}
              </li>
            ) : (
              filtered.map((m) => (
                <li key={m.user_id}>
                  <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(m.user_id)}
                      onChange={() => onToggle(m.user_id)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    {m.picture ? (
                      <img src={m.picture} alt="" className="w-5 h-5 rounded-full shrink-0" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-bold flex items-center justify-center shrink-0">
                        {(m.name || m.email || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-900 truncate">{m.name || m.email}</p>
                      {m.name && m.email && (
                        <p className="text-[10px] text-gray-400 truncate">{m.email}</p>
                      )}
                    </div>
                  </label>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---- CustomerTableView ----

export interface CustomerTableViewProps {
  customers: RivhitCustomer[];
  members: TenantMember[];
  membersByCustomerId: Map<number, TenantMember[]>;
  onUpdateCustomer: (
    customerId: number,
    data: Partial<Pick<RivhitCustomer, 'last_name' | 'first_name' | 'phone' | 'email' | 'city' | 'street'>>,
  ) => Promise<void>;
  onViewDetails: (customer: RivhitCustomer) => void;
}

export default function CustomerTableView({
  customers,
  members,
  membersByCustomerId,
  onUpdateCustomer,
  onViewDetails,
}: CustomerTableViewProps) {
  const { t } = useTranslation();

  // Per-row form edits (only populated for touched rows)
  const [editedValues, setEditedValues] = useState<Map<number, RowFormState>>(new Map());
  // Per-row assignment edits (user IDs, only populated when user has toggled a member)
  const [editedAssignments, setEditedAssignments] = useState<Map<number, Set<string>>>(new Map());
  // Post-save local assignment overrides (updated from API response, avoids full member reload)
  const [localAssignmentOverrides, setLocalAssignmentOverrides] = useState<
    Map<number, CustomerAssignedMember[]>
  >(new Map());
  // Save lifecycle
  const [savingRows, setSavingRows] = useState<Set<number>>(new Set());
  const [errorRows, setErrorRows] = useState<Map<number, string>>(new Map());
  const [savedRows, setSavedRows] = useState<Set<number>>(new Set());

  // ---- Helpers ----

  function getForm(customer: RivhitCustomer): RowFormState {
    return editedValues.get(customer.customer_id) ?? toRowForm(customer);
  }

  function getAssignmentIds(customerId: number): Set<string> {
    if (editedAssignments.has(customerId)) return editedAssignments.get(customerId)!;
    const override = localAssignmentOverrides.get(customerId);
    const base = override ?? membersByCustomerId.get(customerId) ?? [];
    return new Set(base.map((m) => m.user_id));
  }

  function isFieldsDirty(customer: RivhitCustomer): boolean {
    const form = editedValues.get(customer.customer_id);
    if (!form) return false;
    return (
      form.last_name !== (customer.last_name ?? '') ||
      form.first_name !== (customer.first_name ?? '') ||
      form.phone !== (customer.phone ?? '') ||
      form.email !== (customer.email ?? '') ||
      form.city !== (customer.city ?? '')
    );
  }

  function isAssignmentsDirty(customerId: number): boolean {
    if (!editedAssignments.has(customerId)) return false;
    const edited = editedAssignments.get(customerId)!;
    const override = localAssignmentOverrides.get(customerId);
    const base = override ?? membersByCustomerId.get(customerId) ?? [];
    const currentIds = new Set(base.map((m) => m.user_id));
    if (edited.size !== currentIds.size) return true;
    for (const id of edited) if (!currentIds.has(id)) return true;
    return false;
  }

  function isDirty(customer: RivhitCustomer): boolean {
    return isFieldsDirty(customer) || isAssignmentsDirty(customer.customer_id);
  }

  // ---- Handlers ----

  function setFieldValue(customerId: number, field: keyof RowFormState, value: string, customer: RivhitCustomer) {
    setEditedValues((prev) => {
      const current = prev.get(customerId) ?? toRowForm(customer);
      return new Map(prev).set(customerId, { ...current, [field]: value });
    });
  }

  function toggleAssignment(customerId: number, userId: string) {
    const initialIds = getAssignmentIds(customerId);
    setEditedAssignments((prev) => {
      const current = prev.has(customerId) ? new Set(prev.get(customerId)!) : new Set(initialIds);
      if (current.has(userId)) current.delete(userId);
      else current.add(userId);
      return new Map(prev).set(customerId, current);
    });
  }

  async function handleSave(customer: RivhitCustomer) {
    const cid = customer.customer_id;
    const fieldsDirty = isFieldsDirty(customer);
    const assignDirty = isAssignmentsDirty(cid);
    if (!fieldsDirty && !assignDirty) return;

    setSavingRows((prev) => new Set([...prev, cid]));
    setErrorRows((prev) => { const m = new Map(prev); m.delete(cid); return m; });

    try {
      if (fieldsDirty) {
        const form = editedValues.get(cid)!;
        const payload: Partial<Pick<RivhitCustomer, 'last_name' | 'first_name' | 'phone' | 'email' | 'city'>> = {};
        if (form.last_name !== (customer.last_name ?? '')) payload.last_name = form.last_name;
        if (form.first_name !== (customer.first_name ?? '')) payload.first_name = form.first_name;
        if (form.phone !== (customer.phone ?? '')) payload.phone = form.phone;
        if (form.email !== (customer.email ?? '')) payload.email = form.email;
        if (form.city !== (customer.city ?? '')) payload.city = form.city;
        await onUpdateCustomer(cid, payload);
        setEditedValues((prev) => { const m = new Map(prev); m.delete(cid); return m; });
      }

      if (assignDirty) {
        const ids = Array.from(editedAssignments.get(cid)!);
        const result = await setCustomerAssignments(cid, ids);
        setLocalAssignmentOverrides((prev) => new Map(prev).set(cid, result.assigned_members));
        setEditedAssignments((prev) => { const m = new Map(prev); m.delete(cid); return m; });
      }

      setSavedRows((prev) => new Set([...prev, cid]));
      setTimeout(() => setSavedRows((prev) => { const s = new Set(prev); s.delete(cid); return s; }), 2000);
    } catch (err: any) {
      setErrorRows((prev) =>
        new Map(prev).set(
          cid,
          err?.response?.data?.detail || err?.message || t('customers.editError', 'Failed to save'),
        ),
      );
    } finally {
      setSavingRows((prev) => { const s = new Set(prev); s.delete(cid); return s; });
    }
  }

  const inputCls =
    'w-full rounded border border-gray-200 px-2 py-1 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none bg-white disabled:bg-gray-50 disabled:cursor-not-allowed';

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm bg-white">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-3 py-3 ltr:text-left rtl:text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-14">
              ID
            </th>
            <th className="px-3 py-3 ltr:text-left rtl:text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-36">
              {t('customers.lastName', 'Last Name')}
            </th>
            <th className="px-3 py-3 ltr:text-left rtl:text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">
              {t('customers.firstName', 'First Name')}
            </th>
            <th className="px-3 py-3 ltr:text-left rtl:text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-32">
              {t('customers.phone', 'Phone')}
            </th>
            <th className="px-3 py-3 ltr:text-left rtl:text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-44">
              {t('customers.email', 'Email')}
            </th>
            <th className="px-3 py-3 ltr:text-left rtl:text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">
              {t('customers.city', 'City')}
            </th>
            <th className="px-3 py-3 ltr:text-left rtl:text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-44">
              {t('assignments.teamSection', 'Assigned Team')}
            </th>
            <th className="px-3 py-3 w-24" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {customers.length === 0 ? (
            <tr>
              <td colSpan={8} className="px-3 py-8 text-center text-sm text-gray-400">
                {t('customers.noResults', 'No customers found')}
              </td>
            </tr>
          ) : (
            customers.map((customer) => {
              const cid = customer.customer_id;
              const form = getForm(customer);
              const dirty = isDirty(customer);
              const saving = savingRows.has(cid);
              const saved = savedRows.has(cid);
              const error = errorRows.get(cid);
              const assignmentIds = getAssignmentIds(cid);

              return (
                <tr
                  key={cid}
                  data-testid="customer-row"
                  data-dirty={dirty ? 'true' : 'false'}
                  className={[
                    'transition-colors',
                    saving ? 'opacity-60' : '',
                    dirty ? 'bg-amber-100 border-l-4 border-l-amber-400' : 'hover:bg-gray-50',
                  ].join(' ')}
                >
                  {/* ID */}
                  <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{cid}</td>

                  {/* Last Name */}
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      data-testid="customer-field-last_name"
                      value={form.last_name}
                      onChange={(e) => setFieldValue(cid, 'last_name', e.target.value, customer)}
                      disabled={saving}
                      className={inputCls}
                      placeholder={t('customers.lastNamePlaceholder', 'Last name')}
                    />
                  </td>

                  {/* First Name */}
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      data-testid="customer-field-first_name"
                      value={form.first_name}
                      onChange={(e) => setFieldValue(cid, 'first_name', e.target.value, customer)}
                      disabled={saving}
                      className={inputCls}
                      placeholder={t('customers.firstNamePlaceholder', 'First name')}
                    />
                  </td>

                  {/* Phone */}
                  <td className="px-3 py-2">
                    <input
                      type="tel"
                      data-testid="customer-field-phone"
                      value={form.phone}
                      onChange={(e) => setFieldValue(cid, 'phone', e.target.value, customer)}
                      disabled={saving}
                      className={inputCls}
                      dir="ltr"
                      placeholder="050-0000000"
                    />
                  </td>

                  {/* Email */}
                  <td className="px-3 py-2">
                    <input
                      type="email"
                      data-testid="customer-field-email"
                      value={form.email}
                      onChange={(e) => setFieldValue(cid, 'email', e.target.value, customer)}
                      disabled={saving}
                      className={inputCls}
                      dir="ltr"
                      placeholder="email@example.com"
                    />
                  </td>

                  {/* City */}
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      data-testid="customer-field-city"
                      value={form.city}
                      onChange={(e) => setFieldValue(cid, 'city', e.target.value, customer)}
                      disabled={saving}
                      className={inputCls}
                      placeholder={t('customers.cityPlaceholder', 'City')}
                    />
                  </td>

                  {/* Assigned Team */}
                  <td className="px-3 py-2">
                    <TeamCombobox
                      members={members}
                      selectedIds={assignmentIds}
                      onToggle={(userId) => toggleAssignment(cid, userId)}
                      disabled={saving}
                    />
                  </td>

                  {/* Actions */}
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex flex-col items-start gap-1">
                      <div className="flex items-center gap-1.5">
                        {saved && !dirty ? (
                          <span data-testid="customer-saved" className="text-xs text-green-600 font-medium">
                            ✓ {t('common.saved', 'Saved')}
                          </span>
                        ) : (
                          <>
                            <button
                              type="button"
                              data-testid="customer-save"
                              onClick={() => handleSave(customer)}
                              disabled={!dirty || saving}
                              className={[
                                'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                                dirty && !saving
                                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                                  : 'bg-gray-100 text-gray-400 cursor-not-allowed',
                              ].join(' ')}
                            >
                              {saving ? '…' : t('common.save', 'Save')}
                            </button>
                            <button
                              type="button"
                              data-testid="customer-details"
                              aria-label={t('customers.detailsTitle', 'View details')}
                              onClick={() => onViewDetails(customer)}
                              className="rounded px-2 py-1 text-xs font-medium text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors"
                              title={t('customers.detailsTitle', 'View details')}
                            >
                              ↗
                            </button>
                          </>
                        )}
                      </div>
                      {error && (
                        <p className="text-[10px] text-red-600 max-w-[96px] break-words leading-tight">
                          {error}
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
